import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Settings, Webhook, Mail, Key } from "lucide-react";
import AdminThemeToggle from "@/components/AdminThemeToggle";
import { devWarn } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

  useEffect(() => { if (!authLoading && !user) navigate("/admin/login"); }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/admin/settings`, { headers: getHeaders() })
      .then(res => setWebhookUrl(res.data.crm_webhook_url || ""))
      .catch(err => devWarn("Settings load failed:", err.message));
  }, [user]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, { crm_webhook_url: webhookUrl }, { headers: getHeaders() });
      toast.success("Inställningar sparade");
    } catch { toast.error("Kunde inte spara"); }
    finally { setSaving(false); }
  };

  if (authLoading || !user) return <div className="min-h-screen bg-[#0A0A0A]" />;

  const inputCls = "w-full bg-[#1A1A1A] border border-[#262626] px-5 py-3.5 text-white font-['Outfit'] text-sm placeholder:text-white/25 focus:outline-none focus:border-[#C5A059]/50 transition-colors";

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-settings-page">
      <nav className="bg-[#141414] border-b border-[#262626] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/admin" className="text-[#C5A059] font-semibold text-sm tracking-[0.3em] uppercase font-['Outfit']">SERAVA ADMIN</Link>
            <div className="hidden md:flex gap-6">
              <Link to="/admin" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Översikt</Link>
              <Link to="/admin/events" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Events</Link>
              <Link to="/admin/pages" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Sidor</Link>
              <Link to="/admin/orders" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Ordrar</Link>
              <Link to="/admin/contest-entries" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Tävling</Link>
              <Link to="/admin/settings" className="text-white text-xs tracking-wider uppercase font-['Outfit']">Inställningar</Link>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/40 hover:text-white text-xs font-['Outfit']">Visa sajten</Link>
            <AdminThemeToggle />
            <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="text-white/40 hover:text-red-400"><LogOut size={18} /></button>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-['Playfair_Display'] text-3xl text-white mb-8">Inställningar</h1>

        {/* CRM Webhook */}
        <div className="bg-[#141414] border border-[#262626] p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <Webhook size={20} className="text-[#C5A059]" />
            <h2 className="text-white font-['Outfit'] font-medium text-lg">CRM Webhook</h2>
          </div>
          <p className="text-white/50 font-['Outfit'] font-light text-sm mb-6 leading-relaxed">
            Konfigurera webhook-URL för ditt CRM-system. Alla nya ordrar, nyhetsbrevsregistreringar och kontaktförfrågningar skickas till denna URL i realtid.
          </p>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-['Outfit'] uppercase tracking-wider block mb-2">Webhook URL</label>
              <input value={webhookUrl} onChange={e => setWebhookUrl(e.target.value)} placeholder="https://ditt-crm.emergentagent.com/api/webhook/incoming" data-testid="webhook-url-input" className={inputCls} />
            </div>
            <button onClick={saveSettings} disabled={saving} data-testid="save-settings-btn"
              className="btn-primary px-6 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] disabled:opacity-50">
              {saving ? "Sparar..." : "Spara inställningar"}
            </button>
          </div>
        </div>

        {/* Webhook Events Info */}
        <div className="bg-[#141414] border border-[#262626] p-8 mb-6">
          <h3 className="text-white font-['Outfit'] font-medium mb-4">Webhook-händelser</h3>
          <p className="text-white/50 font-['Outfit'] font-light text-sm mb-4">Ditt CRM kommer att ta emot POST-anrop med följande händelsetyper:</p>
          <div className="space-y-3">
            {[
              { event: "new_order", desc: "När en ny order läggs (kortbetalning eller fakturaförfrågan)" },
              { event: "payment_confirmed", desc: "När en kortbetalning genomförs" },
              { event: "newsletter_signup", desc: "När någon prenumererar på nyhetsbrevet" },
              { event: "enquiry_submitted", desc: "När ett kontaktformulär skickas in" },
            ].map(e => (
              <div key={e.event} className="flex gap-4 items-start">
                <code className="text-[#C5A059] text-xs font-mono bg-[#1A1A1A] px-2 py-1 flex-shrink-0">{e.event}</code>
                <span className="text-white/40 text-sm font-['Outfit'] font-light">{e.desc}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CRM Polling Info */}
        <div className="bg-[#141414] border border-[#262626] p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Key size={18} className="text-[#C5A059]" />
            <h3 className="text-white font-['Outfit'] font-medium">CRM Polling API</h3>
          </div>
          <p className="text-white/50 font-['Outfit'] font-light text-sm mb-4">Ditt CRM kan också hämta data via dessa endpoints (kräver admin Bearer-token):</p>
          <div className="space-y-2 font-mono text-xs">
            {[
              "GET /api/crm/orders?since=2026-01-01T00:00:00Z",
              "GET /api/crm/newsletter?since=2026-01-01T00:00:00Z",
              "GET /api/crm/enquiries?since=2026-01-01T00:00:00Z",
            ].map(ep => (
              <div key={ep} className="bg-[#1A1A1A] border border-[#262626] px-4 py-2 text-white/60">{ep}</div>
            ))}
          </div>
        </div>

        {/* Email Config Info */}
        <div className="bg-[#141414] border border-[#262626] p-8">
          <div className="flex items-center gap-3 mb-4">
            <Mail size={18} className="text-[#C5A059]" />
            <h3 className="text-white font-['Outfit'] font-medium">E-postnotiser</h3>
          </div>
          <p className="text-white/50 font-['Outfit'] font-light text-sm mb-2">E-post skickas automatiskt från <span className="text-[#C5A059]">order@seravasports.com</span> via Resend:</p>
          <div className="space-y-2 mt-4">
            {[
              "Orderbekräftelse — skickas när en ny order läggs",
              "Betalningskvitto — skickas när kortbetalning bekräftas",
              "Nyhetsbrevs-välkomst — skickas när någon prenumererar",
              "Kopia till contact@seravasports.com — skickas på alla ordrar & formulär",
            ].map(e => (
              <p key={e} className="text-white/40 text-sm font-['Outfit'] font-light flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full flex-shrink-0" /> {e}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
